import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { saveAs as saveAsFile } from 'file-saver';
import { get, isEmpty, isNil } from 'lodash';
import { useNavigate } from 'hooks';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { setDynamicReportDataToCache } from '../../../campaigns/modules/campaigns-reports/services/setDynamicReportDataToCache';
import setRecommendations from '../../../recommendations/actions/setRecommendations';
import { RECOMMENDATION_SOURCE } from '../../../recommendations/recommendationSource';
import { APP_EVENT_TYPE, useAppEventContext } from '../../app-events-aggregator';
import { intercomService } from '../../intercom';
import { CHAT_AGENT_ACTION_STATUS } from '../config/chatAgentActionStatus';
import { CHAT_AGENT_ACTION_TYPE, CHAT_AGENT_MESSAGE_SOURCE, MANUAL_GA_EVENTS } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT } from '../config/chatAgentComponentGA';
import { analyticsEventTransformer } from '../transformers/analyticsEventTransformer';
import { getActionDetails } from '../utils/eventActionsUtils';
import { getChatAgentGAValue } from '../utils/getChatAgentGAValue';
import { isChatAgentEvent } from '../utils/isChatAgentEvent';
import { updateEventByAction } from '../utils/updateEventByAction';
import { useCanEditCell } from './useCanEditCell';
import { useChatAgentContextService } from './useChatAgentContextService';
import { useChatDialogMessagesConnection } from './useChatDialogMessagesConnection';

export const useChatAgentMessageHandler = ({
    setCellToEdit,
    setWizardStep,
    sendResponseToChatAgent,
    registerAgent,
    unregisterAgent,
    openChat,
    closeChat,
    client,
    setSessionId,
    notifyChatTextMessageListeners,
    getTool,
    setAgentName,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getUrl } = useChatAgentContextService();
    const canEditCell = useCanEditCell();
    const { push: pushAppEvent } = useAppEventContext();

    const { sendUserChatTextMessageToGW, isChatDialogMessage } = useChatDialogMessagesConnection({
        getTool,
        sendResponseToChatAgent,
        setSessionId,
    });
    const handlers = useMemo(
        () => ({
            [CHAT_AGENT_ACTION_TYPE.OPEN_CHAT]: (action, event) => {
                openChat();
                const { type } = getActionDetails(action);
                const updatedEvent = updateEventByAction(event.data, action, {
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    type,
                });
                sendResponseToChatAgent(updatedEvent, event.source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.CLOSE_CHAT]: (action, event) => {
                closeChat();
                const { type } = getActionDetails(action);
                const updatedEvent = updateEventByAction(event.data, action, {
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    type,
                });
                sendResponseToChatAgent(updatedEvent, event.source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.DOWNLOAD_FILE]: (action, event) => {
                const { type, data } = getActionDetails(action);
                const rawData = data?.rawData;

                try {
                    const parsedData = JSON.parse(rawData);
                    const { filename, content, mimeType } = parsedData || {};

                    const blob = new Blob([content], { type: mimeType });
                    saveAsFile(blob, filename);
                } catch (e) {
                    console.error(`Failed to parse rawData: ${rawData}, with error:`, e);
                    return;
                }

                const updatedEvent = updateEventByAction(event.data, action, {
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    type,
                });
                sendResponseToChatAgent(updatedEvent, event.source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.NAVIGATE]: (action, event) => {
                const { data } = getActionDetails(action);
                const url =
                    data?.url ||
                    getUrl({
                        appContext: event.data.appContext,
                        formContext: event.data.formContext,
                    });

                // Promise wrapper applied due to react bug when it batches useEffect callbacks synchronously after navigation
                Promise.resolve().then(() => navigate(url));
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.SET_RECOMMENDED_DATA]: action => {
                const { data } = getActionDetails(action);
                dispatch(
                    setRecommendations({
                        recommendationData: data,
                        recommendationSource: RECOMMENDATION_SOURCE.CHAT_AGENT,
                    })
                );
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.EDIT_GRID_CELL]: action => {
                const { metadata } = getActionDetails(action);
                if (!canEditCell(metadata)) {
                    return;
                }
                setCellToEdit({
                    colId: metadata?.colId,
                    rowId: metadata?.rowId,
                });
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.FORM_WIZARD_STEP]: action => {
                const { data } = getActionDetails(action);
                setWizardStep(data?.rawData);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.ABORT]: () => {
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.CONNECT]: (action, event) => {
                const { source } = event;
                const { type } = getActionDetails(action);

                if (source) {
                    registerAgent(source);
                }

                const updatedEvent = updateEventByAction(event.data, action, {
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    data: {
                        url: `${window.location.pathname}${window.location.search}`,
                    },
                    type,
                });
                sendResponseToChatAgent(updatedEvent, source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.DISCONNECT]: (action, event) => {
                const { type } = getActionDetails(action);
                const { source } = event;
                const updatedEvent = updateEventByAction(event.data, action, {
                    status: CHAT_AGENT_ACTION_STATUS.SUCCESS,
                    type,
                });
                sendResponseToChatAgent(updatedEvent, source);
                unregisterAgent(source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.FORM_ACTION]: () => {
                intercomService.hide();
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.MESSAGE]: action => {
                const { data } = getActionDetails(action);
                notifyChatTextMessageListeners(action);
                if (data?.source === CHAT_AGENT_MESSAGE_SOURCE.USER) {
                    return true;
                }

                openChat();
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.METHOD]: async (action, event) => {
                const { data, type } = getActionDetails(action);
                const { methodName, args } = data;
                const tool = getTool(methodName, action);

                let updatedEvent;
                try {
                    const toolSuccess = await tool(args);
                    updatedEvent = updateEventByAction(event.data, action, {
                        status: toolSuccess ? CHAT_AGENT_ACTION_STATUS.SUCCESS : CHAT_AGENT_ACTION_STATUS.FAILURE,
                        type,
                    });
                } catch (e) {
                    updatedEvent = updateEventByAction(event.data, action, {
                        status: CHAT_AGENT_ACTION_STATUS.FAILURE,
                        type,
                        value: e.message,
                    });
                }

                sendResponseToChatAgent(updatedEvent, event.source);
                return true;
            },
            [CHAT_AGENT_ACTION_TYPE.SET_DYNAMIC_REPORT_DATA]: action => {
                const { data } = getActionDetails(action);
                const { reportId, accountId, reportData } = data;

                if (!reportId || !accountId || !reportData) {
                    console.error('SET_DYNAMIC_REPORT_DATA: reportId, accountId, and reportData are required');
                    return false;
                }

                try {
                    setDynamicReportDataToCache(reportId, accountId, reportData);
                    return true;
                } catch (error) {
                    console.error('SET_DYNAMIC_REPORT_DATA failed:', error);
                    return false;
                }
            },
            [CHAT_AGENT_ACTION_TYPE.ANALYTICS_EVENT]: action => {
                const { data } = getActionDetails(action);
                const { eventType, eventData } = data;

                const transformedEventData = analyticsEventTransformer(eventData);

                pushAppEvent({
                    type: eventType,
                    data: transformedEventData,
                });

                return true;
            },
        }),
        [
            openChat,
            sendResponseToChatAgent,
            closeChat,
            getUrl,
            navigate,
            dispatch,
            canEditCell,
            setCellToEdit,
            setWizardStep,
            registerAgent,
            unregisterAgent,
            notifyChatTextMessageListeners,
            getTool,
            pushAppEvent,
        ]
    );
    const messageHandler = useCallback(
        async event => {
            if (!isChatAgentEvent(event, client)) {
                return;
            }

            if (isChatDialogMessage(event)) {
                sendUserChatTextMessageToGW(event);
                return;
            }

            const actions = get(event, 'data.actions', []);
            const agentName = get(event, 'data.agentName');

            if (!isNil(agentName)) {
                setAgentName(agentName);
            }

            if (isEmpty(actions)) {
                pushAppEvent({
                    type: APP_EVENT_TYPE.CHAT_TRIGGERED_ACTION,
                    status: CHAT_AGENT_ACTION_STATUS.FAILURE,
                    displayText: 'Actions are empty.',
                });
                return;
            }

            for (let action of actions) {
                const { type, data } = getActionDetails(action);

                if (!MANUAL_GA_EVENTS[type]) {
                    gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                        component: CHAT_AGENT_COMPONENT,
                        value: getChatAgentGAValue(action),
                    });
                }

                const handler = handlers[type];
                if (handler) {
                    const result = await handler(action, event);

                    if (type !== CHAT_AGENT_ACTION_TYPE.ANALYTICS_EVENT) {
                        pushAppEvent({
                            type: `${APP_EVENT_TYPE.CHAT_TRIGGERED_ACTION}_${type}`,
                            status: result ? CHAT_AGENT_ACTION_STATUS.SUCCESS : CHAT_AGENT_ACTION_STATUS.FAILURE,
                            data,
                        });
                    }

                    if (!result) {
                        break;
                    }
                }
            }
        },
        [client, isChatDialogMessage, sendUserChatTextMessageToGW, pushAppEvent, handlers, setAgentName]
    );

    return messageHandler;
};
